import { Box, Button, Flex, Heading, Text } from '@weareberyl/design-system'

import { Permission, PermissionOnly } from '~/components/Session'
import type { FeatureFlags } from '~/context/featureFlags'
import useFeatureFlags from '~/hooks/useFeatureFlags'
import { subscribeToPhotoUploadTopic } from '~/utils/firebase/messaging'

const FeatureButton = ({
  feature,
  permission,
  title,
  description,
}: {
  feature: keyof FeatureFlags
  permission: Permission
  title: string
  description?: string
}) => {
  const [featureFlags, setFeatureFlags] = useFeatureFlags()

  const flag = featureFlags[feature]

  return (
    <PermissionOnly permission={permission}>
      <Box mb={2} alignItems="flex-start" width="100%">
        <Heading mb={2} variant="h4">
          {description}
        </Heading>
        <Button
          title={flag ? `Disable ${title}` : `Enable ${title}`}
          variant={flag ? 'outline' : 'primary'}
          onClick={() => {
            setFeatureFlags({
              ...featureFlags,
              [feature]: !flag,
            })
            // Topic subscription otherwise only happens at sign-in, so subscribe
            // now when the flag is switched on mid-session.
            if (feature === 'push_parking_photo' && !flag) {
              subscribeToPhotoUploadTopic()
            }
          }}
        />
      </Box>
    </PermissionOnly>
  )
}

export default function FeatureFlagsComponent() {
  return (
    <Flex flexDirection="col" alignItems="flex-start" gridGap={'20px'}>
      <Heading variant="h1">Feature Flags 🧪</Heading>
      <FeatureButton
        feature="promotions_nav"
        title="Promotion UI"
        description="You can try the Voucherify UI feature as it develops"
        permission="campaign.edit"
      />
      <FeatureButton
        feature="push_parking_photo"
        title="Auto load parking photos"
        description="As soon as parking photo arrives, load it."
        permission="journey.view"
      />
      <FeatureButton
        feature="parking_controls"
        title="Parking controls"
        description="Display end journey controls alongside parking photo"
        permission="journey.view"
      />
      <FeatureButton
        feature="dashboards_nav"
        title="Dashboards"
        description="Show the Dashboards section, linking to Looker dashboards"
        permission="global.view"
      />
      <FeatureButton
        feature="module_qr"
        title="Unlock QR"
        description="Show a little QR code on modual summary"
        permission="global.view"
      />
    </Flex>
  )
}
